import { z } from 'zod';
import type { EngineId } from '../map/core/types';

const EngineSchema = z.enum(['cesium', 'maplibre', 'native']);

const RuntimeConfigSchema = z.object({
  map: z.object({
    engine: EngineSchema,
    fallbackEngines: z.array(EngineSchema).min(1),
    baseTileUrl: z.string().url(),
    baseTileAttribution: z.string().min(1),
    cesiumIonToken: z.string().optional(),
    enableTerrain: z.boolean(),
    enableBuildings: z.boolean(),
  }),
  features: z.object({
    engineSwitcher: z.boolean(),
    drawing: z.boolean(),
    measurements: z.boolean(),
    exports: z.boolean(),
  }),
});

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

const DEFAULT_CONFIG: RuntimeConfig = {
  map: {
    engine: 'cesium',
    fallbackEngines: ['maplibre', 'native'],
    baseTileUrl: 'https://tile.openstreetmap.org/',
    baseTileAttribution: '© OpenStreetMap contributors',
    enableTerrain: false,
    enableBuildings: false,
  },
  features: {
    engineSwitcher: true,
    drawing: true,
    measurements: true,
    exports: true,
  },
};

let configPromise: Promise<RuntimeConfig> | undefined;

export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (!configPromise) {
    const source = import.meta.env.VITE_RUNTIME_CONFIG_URL ?? '/runtime-config.json';
    configPromise = fetchWithTimeout(source, 4_000)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Runtime config failed with status ${response.status}.`);
        const value = await response.json() as Partial<RuntimeConfig>;
        return mergeAndValidate(value);
      })
      .catch((error) => {
        console.warn('[world-twin] Runtime config unavailable or invalid; using safe defaults.', error);
        return DEFAULT_CONFIG;
      });
  }
  return configPromise;
}

export function resetRuntimeConfigCache(): void {
  configPromise = undefined;
}

function mergeAndValidate(input: Partial<RuntimeConfig>): RuntimeConfig {
  const merged = {
    map: {
      ...DEFAULT_CONFIG.map,
      ...(input.map ?? {}),
      cesiumIonToken: input.map?.cesiumIonToken || undefined,
    },
    features: {
      ...DEFAULT_CONFIG.features,
      ...(input.features ?? {}),
    },
  };
  const parsed = RuntimeConfigSchema.safeParse(merged);
  if (!parsed.success) {
    throw new Error(`Invalid runtime configuration: ${z.prettifyError(parsed.error)}`);
  }
  return parsed.data;
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { cache: 'no-store', signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

export function isEngineId(value: unknown): value is EngineId {
  return EngineSchema.safeParse(value).success;
}
